import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ClustersState } from '../Types/GlobalTypes';
import { CompanySWOTAnalysis, CompanySWOTRefresh, GetAccountClusterRequest, GetClusterCompanyNewsRequest, GetProductAndServicesRequest, GetProfileRequest, GetStrategicHighlightsRequest, ManageAccountClusterRequest, ShareAccountClusterRequest } from '../Types/RequestBodies';
import { ClusterDetailsResponse, Clusters, ClustersData, DetailsData, ManageClustersResponse, SelectedCluster, SelectedCompany } from '../Types/ClustersTypes';
import { CompanyProfile, CompanySecFilings, CompanyStockChart, ProductAndServicesData, SWOTRefresh, SWOTResponse } from '../Types/CompaniesTypes';
import { Article } from '../Types/ArticleTypes';
import { Filter } from '../Types/FilterTypes';

function purgeClusterCompanyInsightCache(state: ClustersState, companyIds: number[]) {
  companyIds.forEach((companyId) => {
    delete state.companyProfile[companyId];
    delete state.profileLoading[companyId];
    delete state.strategicHighlights[companyId];
    delete state.strategicHighlightsLoading[companyId];
    delete state.stocksData[companyId];
    delete state.stocksLoading[companyId];
    delete state.swotData[companyId];
    delete state.swotLoading[companyId];
    delete state.companySecFilings[companyId];
    delete state.companySecFilingsLoading[companyId];
  });
}

export const initialState: ClustersState = {
  allClustersLoading: false,
  activeClustersLoading: false,
  activeClustersLoaded: false,
  activeClusters: [],
  activeClustersCount: 0,
  allClusters: [],
  clusterCount: 0,
  clusterCreatedSuccesfully: false,
  clusterPageNumber: 0,
  clustersShared: false,
  selectedClusters: [],
  clusterDrawerOpen: false,
  detailsLoading: false,
  detailsRequestedClusterId: 0,
  clusterCompanies: [],
  clusterCompanyCount: 0,
  clusterId: 0,
  clusterName: '',
  clusterShared: 0,
  sharedByFirstName: '',
  creationSuccess: false,
  selectedClusterCompanies: [],
  profileLoading: {},
  companyProfile: {},
  strategicHighlightsLoading: {},
  strategicHighlights: {},
  stocksLoading: {},
  stocksData: {},
  swotLoading: {},
  swotRefreshLoading: {},
  swotRefreshedCompany: 0,
  swotData: {},
  companySecFilingsLoading: {},
  companySecFilings: {},
  companyNewsLoading: false,
  companyNewsPageNumber: 1,
  companyNewsFiltersLoading: false,
  companyNews: [],
  companyNewsEnded: false,
  companyNewsFilters: [],
  companyNewsFiltersSelected: '',
  newsFilterApplied: false,
  refreshCompanyIds: [],
  companyNewsFiltersApplied: '',
  companyNewsCompanyIdsApplied: '',
  productAndServicesLoading: {},
  productAndServices: {},

  accountPlanUrl: '',
  accountPlanPreviewUrl: '',
  accountPlanAllowed: false,
  sapAllowed: false,
  newNotes: 0,
  companyNotesAvailable: 0,
  accountPlanAllowDownload: false,
  allCompanyDetailsLoading: false,
  clusterSortingType: '',
}
const ClustersSlice = createSlice({
  name: 'clusters',
  initialState,
  reducers: {
    setDrawerOpen(state, action: PayloadAction<boolean>) {
      state.clusterDrawerOpen = action.payload;
    },
    getAccountClusters(state, action: PayloadAction<GetAccountClusterRequest>) {
      if (action.payload.type === 'active') {
        const isFirstPage = !action.payload.page || action.payload.page <= 1;
        if (isFirstPage && !state.activeClustersLoading) {
          state.activeClustersLoaded = false;
          state.activeClusters = [];
        }
        state.activeClustersLoading = true;
      } else {
        if (action.payload.page === 1) {
          state.allClusters = [];
          state.clusterCount = 0;
        }
        state.allClustersLoading = true;
      }
    },
    setAllClusters(state, action: PayloadAction<{ results: ClustersData, page?: number, type?: string }>) {
      state.allClustersLoading = false;
      state.activeClustersLoading = false;
      if (action.payload.type === 'active') {
        state.activeClustersLoaded = true;
        state.activeClustersCount = action.payload.results.clusterCount
        if (action.payload?.page === 1) {
          state.activeClusters = action.payload.results.clusters;
        }
      } else {
        state.clusterCount = action.payload.results.clusterCount;
        if (action.payload?.page === 1) {
          state.allClusters = action.payload.results.clusters;
        } else {
          state.allClusters = [
            ...state.allClusters,
            ...action.payload.results.clusters,
          ]
        }
        state.clusterPageNumber += 1;
      }
    },
    accountClustersRequestFailed(state, action: PayloadAction<{ type?: string }>) {
      state.allClustersLoading = false;
      state.activeClustersLoading = false;
      if (action.payload.type === 'active') {
        state.activeClustersLoaded = true;
      }
    },
    manageAccountCluster(state, action: PayloadAction<ManageAccountClusterRequest>) {
      if (action.payload.type === 'edit') {
        state.detailsLoading = true;
      }
      if (action.payload.type === 'create') {
        state.detailsLoading = true;
        state.clusterCompanies = [];
        state.creationSuccess = false;
      }
    },
    createCluster(state, actions: PayloadAction<ManageClustersResponse>) {
      if (actions.payload.response) {
        state.creationSuccess = true;
        state.clusterId = actions.payload.response.clusterId;
        state.activeClusters.unshift(actions.payload.response);
        // state.allClusters.unshift(actions.payload.response);
        // state.allClusters.pop();
      }
    },
    deleteCluster(state, action: PayloadAction<ManageClustersResponse>) {
      const { payload } = action;
      state.clusterCount = state.clusterCount - 1;
      state.activeClustersCount = state.activeClustersCount - 1;
      state.allClusters = state.allClusters.filter(
        (cluster: Clusters) => cluster.clusterId !== payload.response.clusterId
      );
      state.activeClusters = state.activeClusters.filter(
        (cluster: Clusters) => cluster.clusterId !== payload.response.clusterId
      )
    },
    shareAccountClusters(state, action: PayloadAction<ShareAccountClusterRequest>) {
    },
    setShareStatus(state, action: PayloadAction<boolean>) {
      state.clustersShared = action.payload;
      state.clusterDrawerOpen = false;
    },
    setSelectedClusters(state, action: PayloadAction<SelectedCluster[]>) {
      const { payload } = action;
      state.selectedClusters = payload;
    },
    setClusterSortingType(state, action: PayloadAction<string>) {
      state.clusterSortingType = action.payload;
    },
    getClusterDetails(state, action: PayloadAction<{ clusterId: number, sortingType?: string }>) {
      const { clusterId, sortingType } = action.payload;
      if (state.clusterId === clusterId && state.clusterCompanies.length > 0 && !sortingType) {
        return;
      }
      if (state.detailsLoading && state.detailsRequestedClusterId === clusterId) {
        return;
      }
      state.detailsRequestedClusterId = clusterId;
      state.detailsLoading = true;
      if (state.clusterId !== clusterId) {
        state.clusterCompanies = [];
        state.clusterName = '';
      }
    },
    setClusterDetails(state, action: PayloadAction<DetailsData>) {
      state.detailsLoading = false;
      state.detailsRequestedClusterId = 0;
      state.clusterId = action.payload.clusterId;
      state.clusterName = action.payload.clusterName;
      state.clusterShared = action.payload.clusterShared;
      state.clusterCompanies = action.payload.companies;
      state.clusterCompanyCount = action.payload.companyCount;
      state.sharedByFirstName = action.payload.sharedByFirstName;
    },
    updateClusterDetails(state, action: PayloadAction<ManageClustersResponse>) {
      state.detailsLoading = false;
      const { response } = action.payload;
      const newCompanies = response.companiesData ?? [];
      const newCompanyIds = new Set(newCompanies.map((company) => company.companyId));
      const removedCompanyIds = state.clusterCompanies
        .filter((company) => !newCompanyIds.has(company.companyId))
        .map((company) => company.companyId);

      if (removedCompanyIds.length > 0) {
        purgeClusterCompanyInsightCache(state, removedCompanyIds);
        state.selectedClusterCompanies = state.selectedClusterCompanies.filter(
          (company) => newCompanyIds.has(company.companyId),
        );
        state.companyNews = [];
        state.companyNewsPageNumber = 1;
        state.companyNewsEnded = false;
        state.companyNewsFiltersSelected = '';
        state.newsFilterApplied = false;
      }

      state.clusterId = response.clusterId;
      state.clusterName = response.clusterName;
      state.clusterCompanies = newCompanies;
      state.clusterCompanyCount = response.companyCount;
    },
    setEditErrorState(state) {
      state.detailsLoading = false;
    },
    resetClusterManageLoading(state) {
      state.detailsLoading = false;
      state.detailsRequestedClusterId = 0;
    },
    resetClusterDetails(state) {
      state.clusterId = 0;
      state.clusterName = '';
      state.clusterShared = 0;
      state.clusterCompanies = [];
      state.clusterCompanyCount = 0;
      state.sharedByFirstName = '';
      state.detailsRequestedClusterId = 0;
    },
    resetClusterCreationStatus(state, action: PayloadAction<boolean>) {
      state.creationSuccess = action.payload;
    },
    setClusterSelectedCompanies(state, action: PayloadAction<SelectedCompany[]>) {
      state.selectedClusterCompanies = action.payload;
    },
    resetClusterSelectedCompanies(state) {
      state.selectedClusterCompanies = [];
    },
    toggleClusterSelectedCompany(state, action: PayloadAction<SelectedCompany>) {
      const { companyId } = action.payload;
      const exists = state.selectedClusterCompanies.some(
        (company) => company.companyId === companyId,
      );
      state.selectedClusterCompanies = exists
        ? state.selectedClusterCompanies.filter((company) => company.companyId !== companyId)
        : [...state.selectedClusterCompanies, action.payload];
    },
    pruneClusterSelectedCompanies(state, action: PayloadAction<number[]>) {
      const validCompanyIds = new Set(action.payload);
      state.selectedClusterCompanies = state.selectedClusterCompanies.filter(
        (company) => validCompanyIds.has(company.companyId),
      );
    },
    removeClusterSelectedCompanies(state, action: PayloadAction<number[]>) {
      const removedIds = new Set(action.payload);
      state.selectedClusterCompanies = state.selectedClusterCompanies.filter(
        (company) => !removedIds.has(company.companyId),
      );
    },
    setAllCompanyDetailsLoading(state, action: PayloadAction<boolean>) {
      state.allCompanyDetailsLoading = action.payload;
    },
    getClusterCompanyProfile(state, action: PayloadAction<GetProfileRequest>) {
      state.profileLoading[action.payload.companyId] = true;
    },
    setClusterCompanyProfile(state, action: PayloadAction<{ companyId: number, profile: CompanyProfile | null }>) {
      const { companyId, profile } = action.payload;
      if (profile) {
        state.companyProfile = {
          ...state.companyProfile,
          [companyId]: profile,
        };
      }
      state.profileLoading[companyId] = false;
      state.allCompanyDetailsLoading = false;
    },
    getClusterStrategicHighlight(state, action: PayloadAction<{ id: number, type: string, key: string }>) {
      state.strategicHighlightsLoading[action.payload.id] = true;
    },
    setClusterStrategicHighlight(state, action: PayloadAction<{ id: number, highlights: any[] }>) {
      state.strategicHighlights[action.payload.id] = action.payload.highlights;
      state.strategicHighlightsLoading[action.payload.id] = false;
      state.allCompanyDetailsLoading = false;
    },
    getClusterStocksChart(state, action: PayloadAction<GetProfileRequest>) {
      state.stocksLoading[action.payload.companyId] = true;
    },
    setClusterStocksChart(state, action: PayloadAction<{ companyId: number, charts: CompanyStockChart }>) {
      state.stocksLoading[action.payload.companyId] = false;
      state.stocksData[action.payload.companyId] = action.payload.charts;
      state.allCompanyDetailsLoading = false;
    },
    getClusterSWOTAnalysis(state, action: PayloadAction<CompanySWOTAnalysis>) {
      state.swotLoading[action.payload.companyId] = true;
    },
    setClusterSWOTAnalysis(state, action: PayloadAction<{ companyId: number, results: SWOTResponse }>) {
      state.swotData[action.payload.companyId] = action.payload.results;
      state.swotLoading[action.payload.companyId] = false;
      state.allCompanyDetailsLoading = false;
    },
    setClusterSWOTAnalysisFailed(state) {
      state.swotLoading = initialState.swotLoading;
      state.swotData = initialState.swotData;
      state.allCompanyDetailsLoading = false;
    },
    getClusterCompanySecFillings(state, action: PayloadAction<GetProfileRequest>) {
      state.companySecFilingsLoading[action.payload.companyId] = true;
    },
    setClusterCompanySecFillings(state, action: PayloadAction<{ companyId: number, results: CompanySecFilings | null }>) {
      state.companySecFilings[action.payload.companyId] = action.payload.results;
      state.companySecFilingsLoading[action.payload.companyId] = false;
    },
    getClusterCompanyNews(state, action: PayloadAction<GetClusterCompanyNewsRequest>) {
      state.companyNewsLoading = true;
      if (action.payload.page === 1) {
        state.companyNewsFiltersLoading = true;
        state.companyNews = [];
        state.companyNewsPageNumber = 1;
      }
    },
    setClusterCompanyNews(state, action: PayloadAction<Article[] | null>) {
      state.companyNewsLoading = false;

      const incomingArticles = action.payload ?? [];
      const existingIds = new Set(state.companyNews?.map((article) => article.articleId));
      const uniqueArticles = incomingArticles.filter((article) => !existingIds.has(article.articleId));

      if (state.companyNews === null || state.companyNewsPageNumber === 1) {
        state.companyNews = uniqueArticles;
      } else if (action.payload) {
        state.companyNews = [...(state.companyNews ?? []), ...uniqueArticles];
      }
      state.companyNewsPageNumber += 1;
      state.companyNewsEnded = !action.payload?.length;
    },

    setClusterNewsFilters(state, action: PayloadAction<Filter[]>) {
      state.companyNewsFiltersLoading = false;
      if (state.companyNewsFilters.length !== action.payload.length) {
        state.companyNewsFilters = action.payload;
      }
    },
    setClusterCompanyNewsPageNumber(state, action: PayloadAction<number>) {
      state.companyNewsPageNumber = action.payload;
    },
    setClusterCompanyNewsFiltersSelected(state, action: PayloadAction<string>) {
      state.companyNewsFiltersSelected = action.payload;
    },
    resetClusterCompanyNews(state, action) {
      state.companyNews = null;
      state.companyNewsLoading = true;
      state.companyNewsPageNumber = 1;
      state.companyNewsEnded = false;
      state.companyNewsFiltersLoading = true;
      state.companyNewsFilters = [];
      state.companyNewsFiltersSelected = '';
      state.companyNewsCompanyIdsApplied = '';
    },
    setClusterCompanyNewsFiltersApplied(state, action: PayloadAction<string>) {
      state.companyNewsFiltersApplied = action.payload;
    },
    setClusterCompanyNewsCompanyIdsApplied(state, action: PayloadAction<string>) {
      state.companyNewsCompanyIdsApplied = action.payload;
    },
    setClusterNewsFilterApplied(state) {
      state.newsFilterApplied = true;
    },
    resetNewsFilterApplied(state) {
      state.newsFilterApplied = false;
    },
    refreshClusterSWOTAnalysis(state, action: PayloadAction<CompanySWOTRefresh>) {
      const { companyId } = action.payload
      state.swotRefreshLoading[companyId] = true;
      state.refreshCompanyIds = [...state.refreshCompanyIds, companyId];
    },
    removeRefreshClusterCompanyId(state, action: PayloadAction<number>) {
      state.refreshCompanyIds = state.refreshCompanyIds.filter(
        (id) => id !== action.payload,
      );
    },
    setRefreshClusterSWOTAnalysis(state, action: PayloadAction<{ companyId: number, data: SWOTRefresh }>) {
      const { status, flag } = action.payload.data;
      state.swotRefreshLoading[action.payload.companyId] = false;
      state.swotData[action.payload.companyId].status = status;
      if (flag === 'updated') {
        state.swotData[action.payload.companyId].refresh = true;
      }
    },
    setClusterSwotRefreshedCompany(state, action: PayloadAction<number>) {
      state.swotRefreshedCompany = action.payload;
    },
    setRefreshClusterSWOTLoading(state, action: PayloadAction<{ companyId: number, flag: boolean }>) {
      state.swotRefreshLoading[action.payload.companyId] = action.payload.flag;
    },
    getClusterProductAndServices(state, action: PayloadAction<GetProductAndServicesRequest>) {
      state.productAndServicesLoading[action.payload.companyId] = true;
    },
    setClusterProductAndServices(state, action: PayloadAction<{ companyId: number, results: ProductAndServicesData }>) {
      state.productAndServices[action.payload.companyId] = action.payload.results;
      state.productAndServicesLoading[action.payload.companyId] = false;
    },
  }
})

export const { actions, reducer, name: sliceKey } = ClustersSlice;